import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Dialog, useToast, VisuallyHidden, type BadgeTone } from '@artosera/ui';
import { usePeut } from '../../../../auth/usePeut.js';
import { useElevation } from '../../../../components/useElevation.js';
import { formaterDate } from '../../../../lib/format.js';
import {
  gestePlan,
  lancerPlan,
  NATURES_PAR_ETAPE,
  NOMBRE_ETAPES,
  obtenirPlan,
  planActif,
  sceller,
  type EtapePlan,
  type EtatPlan,
  type GestePlan,
  type LotDuPlan,
  type PlanVue,
} from '../../../../lib/plan.js';
import {
  exigeSecondFacteur,
  listerLots,
  lotEnTravail,
  lotExecute,
  lotScelle,
  type LotReprise,
} from '../../../../lib/reprise.js';
import pageStyles from '../../../../styles/page.module.css';
import { cleLots } from '../LotsBloc.js';
import { TON_ETAT_LOT, messageReprise } from '../messages.js';
import reprise from '../Reprise.module.css';
import styles from './Plan.module.css';

export function clePlan(idGalerie: string): readonly string[] {
  return ['console', 'galerie', idGalerie, 'reprise', 'plan'];
}

const TON_PLAN: Record<EtatPlan, BadgeTone> = {
  en_cours: 'reserved',
  en_pause: 'neutral',
  bloque: 'sold',
  termine: 'available',
  abandonne: 'withdrawn',
};

/** Les lots prêts qu'un plan lancerait : un lot sans morceaux, ou chaque morceau prêt. */
export function lotsPrets(lots: readonly LotReprise[]): number {
  return lots.reduce((total, lot) => {
    const morceaux = lot.morceaux?.liste ?? [];
    if (morceaux.length > 0) return total + morceaux.filter((m) => m.etat === 'pret').length;
    return total + (lot.etat === 'pret' ? 1 : 0);
  }, 0);
}

type Statut = 'faite' | 'courante' | 'attente' | 'absente';

function statutDeLEtape(plan: PlanVue | null, rang: number, etape: EtapePlan | undefined): Statut {
  if (!plan || !etape) return 'absente';
  if (etape.termines === etape.lots) return 'faite';
  return plan.etape === rang && planActif(plan) ? 'courante' : 'attente';
}

/**
 * Le plan de reprise (ARTO-132), en tête de l'onglet : les étapes et
 * leurs lots, l'étape courante, l'état du plan, ses gestes (lancer, pause,
 * reprendre, abandonner), la raison d'un arrêt avec un lien vers les lots en
 * cause, et le scellement. Tant que le plan tourne, il se relit toutes les
 * 3 secondes.
 */
export function PlanBloc({ idGalerie }: { idGalerie: string }): JSX.Element {
  const { t } = useTranslation('console');
  const peut = usePeut();
  const client = useQueryClient();
  const { toast } = useToast();
  const elevation = useElevation();
  const [abandon, setAbandon] = useState(false);
  const [scellement, setScellement] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  const plan = useQuery({
    queryKey: clePlan(idGalerie),
    queryFn: () => obtenirPlan(idGalerie),
    refetchInterval: (requete) => (requete.state.data?.etat === 'en_cours' ? 3000 : false),
  });
  const lots = useQuery({
    queryKey: cleLots(idGalerie),
    queryFn: () => listerLots(idGalerie),
    // Un plan lance des lots de lui-même : la liste se relit tant qu'il tourne.
    refetchInterval: (requete) =>
      plan.data?.etat === 'en_cours' || requete.state.data?.some(lotEnTravail) ? 3000 : false,
  });

  const vue = plan.data ?? null;
  const actif = planActif(vue);
  const touteLaListe = lots.data ?? [];
  const prets = lotsPrets(touteLaListe);
  const executes = touteLaListe.filter(lotExecute).length;
  const scelles = touteLaListe.filter(lotScelle).length;
  const peutScellerLeLot = peut('console:rollback-import');
  const secondFacteur = touteLaListe.some(exigeSecondFacteur);

  const apres = (suivant: PlanVue, message: string): void => {
    setErreur(null);
    client.setQueryData(clePlan(idGalerie), suivant);
    void client.invalidateQueries({ queryKey: cleLots(idGalerie) });
    toast({ title: message, closeLabel: t('fiche.domaines.fermer') });
  };
  const echec = (cause: unknown): void => {
    setErreur(messageReprise(t, cause));
    // Le plan a pu changer sous nos yeux (`plan_modifie`, `plan_absent`).
    void client.invalidateQueries({ queryKey: clePlan(idGalerie) });
  };

  const lancer = useMutation({
    mutationFn: () => elevation.executer(() => lancerPlan(idGalerie)),
    onSuccess: (suivant) => apres(suivant, t('plan.plan.gestes.lance')),
    onError: echec,
  });
  const geste = useMutation({
    mutationFn: (g: GestePlan) =>
      g === 'reprendre'
        ? elevation.executer(() => gestePlan(idGalerie, g))
        : gestePlan(idGalerie, g),
    onSuccess: (suivant, g) => {
      setAbandon(false);
      apres(
        suivant,
        t(
          g === 'pause'
            ? 'plan.plan.gestes.enPause'
            : g === 'reprendre'
              ? 'plan.plan.gestes.repris'
              : 'plan.plan.gestes.abandonne',
        ),
      );
    },
    onError: (cause) => {
      setAbandon(false);
      echec(cause);
    },
  });
  const sceller_ = useMutation({
    mutationFn: () => elevation.executer(() => sceller(idGalerie)),
    onSuccess: (resultat) => {
      setScellement(false);
      setErreur(null);
      void client.invalidateQueries({ queryKey: cleLots(idGalerie) });
      void client.invalidateQueries({ queryKey: clePlan(idGalerie) });
      toast({
        title: t('plan.plan.sceller.ok', { count: resultat.scelles }),
        closeLabel: t('fiche.domaines.fermer'),
      });
    },
    onError: (cause) => {
      setScellement(false);
      echec(cause);
    },
  });

  const enAction = lancer.isPending || geste.isPending || sceller_.isPending;
  const lotsDeLArret = new Set(vue?.arret?.lots ?? []);
  const nature = (cle: string): string => String(t(`reprise.natures.${cle}`));

  return (
    <section className={pageStyles.panneau} aria-labelledby="reprise-plan">
      <div className={pageStyles.sectionTitre}>
        <h2 id="reprise-plan">{t('plan.plan.titre')}</h2>
        {vue ? <Badge tone={TON_PLAN[vue.etat]}>{t(`plan.plan.etats.${vue.etat}`)}</Badge> : null}
      </div>
      <div className={reprise.pile}>
        <p className={reprise.texte}>{t('plan.plan.texte')}</p>

        {plan.isLoading ? <p className={pageStyles.videEtat}>{t('plan.plan.chargement')}</p> : null}
        {plan.isError ? (
          <p className={reprise.erreur} role="alert">
            {messageReprise(t, plan.error) ?? t('plan.plan.erreur')}
          </p>
        ) : null}

        {vue ? (
          <div className={styles.entete}>
            <p className={styles.resume}>
              {t('plan.plan.resume', { etape: vue.etape, total: NOMBRE_ETAPES })}
            </p>
            <span className={reprise.discret}>
              {t('plan.plan.meta', {
                operateur: vue.operateur,
                date: formaterDate(vue.creeLe, '—'),
              })}
            </span>
            {vue.termineLe ? (
              <span className={reprise.discret}>
                {t('plan.plan.termineLe', { date: formaterDate(vue.termineLe, '—') })}
              </span>
            ) : null}
          </div>
        ) : plan.isSuccess ? (
          <p className={pageStyles.videEtat}>{t('plan.plan.aucun')}</p>
        ) : null}

        {plan.isSuccess ? (
          <ol className={styles.etapes} aria-label={t('plan.plan.etapes.titre')}>
            {NATURES_PAR_ETAPE.map(({ etape: rang, natures }) => {
              const etape = vue?.etapes.find((candidate) => candidate.etape === rang);
              const statut = statutDeLEtape(vue, rang, etape);
              const lotsEtape: LotDuPlan[] = vue?.lots.filter((lot) => lot.etape === rang) ?? [];
              return (
                <li key={rang} className={styles.etape} data-statut={statut}>
                  <span className={styles.etapeNumero} aria-hidden="true">
                    {rang}
                  </span>
                  <div className={styles.etapeCorps}>
                    <h3 className={styles.etapeTitre}>
                      <VisuallyHidden>
                        {t('plan.plan.etapes.numero', { etape: rang })} :{' '}
                      </VisuallyHidden>
                      {natures.map(nature).join(', ')}
                    </h3>
                    {lotsEtape.length === 0 ? (
                      <span className={reprise.discret}>{t('plan.plan.etapes.sansLot')}</span>
                    ) : (
                      <ul
                        className={styles.lotsEtape}
                        aria-label={t('plan.plan.etapes.lotsDeLEtape', { etape: rang })}
                      >
                        {lotsEtape.map((lot) => (
                          <li key={lot.id}>
                            <Link
                              className={styles.pastille}
                              to={`?lot=${encodeURIComponent(lot.id)}`}
                              data-en-cause={lotsDeLArret.has(lot.id)}
                              aria-label={t('plan.arret.ouvrir', { nature: nature(lot.nature) })}
                            >
                              {nature(lot.nature)}
                              {lot.etat ? (
                                <Badge tone={TON_ETAT_LOT[lot.etat]}>
                                  {t(`reprise.etats.${lot.etat}`)}
                                </Badge>
                              ) : null}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className={styles.etapeStatut}>
                    {etape ? (
                      <>
                        <span>
                          {statut === 'courante'
                            ? t('plan.plan.etapes.courante')
                            : statut === 'faite'
                              ? t('plan.plan.etapes.faite')
                              : t('plan.plan.etapes.attente')}
                        </span>
                        <span className={reprise.discret}>
                          {t('plan.plan.etapes.progression', {
                            termines: etape.termines,
                            lots: etape.lots,
                          })}
                        </span>
                        {etape.echoues > 0 ? (
                          <Badge tone="sold">
                            {t('plan.plan.etapes.echoues', { count: etape.echoues })}
                          </Badge>
                        ) : null}
                        {etape.enCours > 0 ? (
                          <Badge tone="reserved">
                            {t('plan.plan.etapes.enCours', { count: etape.enCours })}
                          </Badge>
                        ) : null}
                      </>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ol>
        ) : null}

        {vue?.etat === 'bloque' && vue.arret ? (
          <div className={styles.arret} role="alert">
            <h3>{t('plan.plan.arret.titre')}</h3>
            <p>
              {t(`plan.plan.arret.raisons.${vue.arret.raison}`, {
                defaultValue: t('plan.plan.arret.raisons.inconnue', { raison: vue.arret.raison }),
              })}
            </p>
            {vue.arret.lots.length > 0 ? (
              <>
                <span className={reprise.discret}>{t('plan.plan.arret.lotsEnCause')}</span>
                <ul className={styles.lotsEtape}>
                  {vue.arret.lots.map((id) => {
                    const lot = vue.lots.find((candidate) => candidate.id === id);
                    return (
                      <li key={id}>
                        <Link
                          className={styles.pastille}
                          to={`?lot=${encodeURIComponent(id)}`}
                          data-en-cause="true"
                        >
                          {lot ? t('plan.plan.arret.ouvrir', { nature: nature(lot.nature) }) : id}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </>
            ) : null}
          </div>
        ) : null}

        {erreur ? (
          <p className={reprise.erreur} role="alert">
            {erreur}
          </p>
        ) : null}

        {plan.isSuccess ? (
          <div className={reprise.pile}>
            <div className={reprise.actions}>
              {!actif ? (
                <Button
                  loading={lancer.isPending}
                  disabled={prets === 0 || enAction}
                  onClick={() => lancer.mutate()}
                >
                  {t('plan.plan.gestes.lancer')}
                </Button>
              ) : null}
              {vue?.etat === 'en_cours' ? (
                <Button
                  variant="secondary"
                  loading={geste.isPending && geste.variables === 'pause'}
                  disabled={enAction}
                  onClick={() => geste.mutate('pause')}
                >
                  {t('plan.plan.gestes.pause')}
                </Button>
              ) : null}
              {vue?.etat === 'en_pause' || vue?.etat === 'bloque' ? (
                <Button
                  loading={geste.isPending && geste.variables === 'reprendre'}
                  disabled={enAction}
                  onClick={() => geste.mutate('reprendre')}
                >
                  {t('plan.plan.gestes.reprendre')}
                </Button>
              ) : null}
              {actif ? (
                <Button variant="ghost" disabled={enAction} onClick={() => setAbandon(true)}>
                  {t('plan.plan.gestes.abandonner')}
                </Button>
              ) : null}
            </div>
            {!actif && prets > 0 ? (
              <p className={reprise.discret}>
                {t('plan.plan.gestes.lancerAide', { count: prets })}
              </p>
            ) : null}
            {!actif && prets === 0 ? (
              <p className={reprise.discret}>{t('plan.plan.aucunPret')}</p>
            ) : null}
            {!actif && prets > 0 && secondFacteur ? (
              <p className={reprise.discret}>{t('plan.plan.gestes.mfa')}</p>
            ) : null}
            {vue?.etat === 'en_cours' ? (
              <p className={reprise.discret} aria-live="polite">
                {t('plan.plan.gestes.pauseAide')} {t('plan.plan.rafraichi')}
              </p>
            ) : null}
            {vue?.etat === 'en_pause' || vue?.etat === 'bloque' ? (
              <p className={reprise.discret}>{t('plan.plan.gestes.reprendreAide')}</p>
            ) : null}
          </div>
        ) : null}

        {peutScellerLeLot && plan.isSuccess ? (
          <div className={styles.sceller}>
            <h3 className={reprise.sousTitre}>{t('plan.plan.sceller.titre')}</h3>
            <p className={reprise.texte}>{t('plan.plan.sceller.texte')}</p>
            {scelles > 0 ? (
              <p className={reprise.discret}>
                <Badge tone="available">{t('reprise.etats.scelle')}</Badge>{' '}
                {t('plan.plan.sceller.scelleLe', { count: scelles })}
              </p>
            ) : null}
            <div className={reprise.actions}>
              <Button
                variant="secondary"
                disabled={actif || executes === 0 || enAction}
                onClick={() => setScellement(true)}
              >
                {t('plan.plan.sceller.bouton')}
              </Button>
            </div>
            {actif ? (
              <p className={reprise.discret}>{t('plan.plan.sceller.planActif')}</p>
            ) : executes === 0 ? (
              <p className={reprise.discret}>{t('plan.plan.sceller.rien')}</p>
            ) : null}
          </div>
        ) : null}
      </div>

      <Dialog
        open={abandon}
        onOpenChange={(ouvert) => {
          if (!ouvert) setAbandon(false);
        }}
        title={t('plan.plan.abandon.titre')}
        description={t('plan.plan.abandon.texte')}
        closeLabel={t('plan.plan.abandon.garder')}
        footer={
          <>
            <Button variant="ghost" onClick={() => setAbandon(false)}>
              {t('plan.plan.abandon.garder')}
            </Button>
            <Button loading={geste.isPending} onClick={() => geste.mutate('abandonner')}>
              {t('plan.plan.abandon.confirmer')}
            </Button>
          </>
        }
      />

      <Dialog
        open={scellement && !elevation.enElevation}
        onOpenChange={(ouvert) => {
          if (!ouvert) setScellement(false);
        }}
        title={t('plan.plan.sceller.confirmationTitre')}
        description={t('plan.plan.sceller.confirmationTexte')}
        closeLabel={t('plan.plan.sceller.garder')}
        footer={
          <>
            <Button variant="ghost" onClick={() => setScellement(false)}>
              {t('plan.plan.sceller.garder')}
            </Button>
            <Button loading={sceller_.isPending} onClick={() => sceller_.mutate()}>
              {t('plan.plan.sceller.confirmer')}
            </Button>
          </>
        }
      >
        <p className={reprise.avertissement} role="note">
          {t('plan.plan.sceller.confirmationMfa')}
        </p>
      </Dialog>
      {elevation.dialogue}
    </section>
  );
}
